import PropTypes from "prop-types";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { AccordionDetails, IconButton, makeStyles, useTheme } from "@material-ui/core";
import { MyLocation } from "@material-ui/icons";

import Typography from "../../../../components/Helpers/Typography";
import { DateService } from "../../../../services/dates.service";
import { moduleMap } from "../../../../services/maps/keyMaps";
import { capitalizeFirstWord } from "../../../../services/text-formatter.service";
import { TimelineEvent } from "../../../../services/timeline.service";
import { vehicleHistoryStyles } from "./sharedStyles";
import { Badge } from "./Badges";
import { FleetContext } from "../../../../context";

const useStyles = makeStyles(vehicleHistoryStyles);

export const TimelineAccordionDetails = ({ event, isVisible, timelineStatus, handleClick }) => {
  const { eventsMap } = useContext(FleetContext);
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  return (
    <AccordionDetails classes={{ root: classes.timelineTripRoot }}>
      <div
        className={classes.timelineTripWrapper}
        style={{
          background: isVisible ? theme.palette.background.selected : null,
        }}
      >
        <div className={classes.eventType}>
          <div className={classes.eventIcon}>
            <Badge type={event.type} />
          </div>
          <div className={classes.eventDetails}>
            <Typography variant="subtitle1">{t(TimelineEvent.getTitle(event))}</Typography>
            <Typography color="secondary">
              {DateService.format(event.timestamp, "hh:mm:ss")}
            </Typography>
          </div>
        </div>
        <div className={classes.summaryWrapper}>
          <div className={classes.stats}>
            <div className={`${classes.dataField} ${classes.smallDataField}`}>
              <Typography color="secondary" className={classes.summaryKeys}>
                {t("Vehicles.Detail.History.Description")}
              </Typography>
            </div>
            <div className={classes.dataField}>
              <Typography className={classes.eventDescription}>
                {t(TimelineEvent.getDescription(eventsMap, event))}
              </Typography>
            </div>
          </div>
          {event.type === "tripEnd" && timelineStatus.getDuration() && (
            <div>
              <div className={classes.stats}>
                <div className={`${classes.dataField} ${classes.smallDataField}`}>
                  <Typography color="secondary" className={classes.summaryKeys}>
                    {t("Vehicles.Detail.History.Distance")}
                  </Typography>
                </div>
                <div className={classes.dataField}>
                  <Typography className={classes.eventDescription}>
                    {t(timelineStatus.displayDistance())}
                  </Typography>
                </div>
              </div>
              <div className={classes.stats}>
                <div className={`${classes.dataField} ${classes.smallDataField}`}>
                  <Typography color="secondary" className={classes.summaryKeys}>
                    {t("Vehicles.Detail.History.Duration")}
                  </Typography>
                </div>
                <div className={classes.dataField}>
                  <Typography className={classes.eventDescription}>
                    {t(timelineStatus.displayDuration())}
                  </Typography>
                </div>
              </div>
              <div className={classes.stats}>
                <div className={`${classes.dataField} ${classes.smallDataField}`}>
                  <Typography color="secondary" className={classes.summaryKeys}>
                    {t("Vehicles.Detail.History.AvgSpeed")}
                  </Typography>
                </div>
                <div className={classes.dataField}>
                  <Typography className={classes.eventDescription}>
                    {t(timelineStatus.displayAvgSpeed())}
                  </Typography>
                </div>
              </div>
            </div>
          )}
          {!["tripStart", "tripEnd", "info", "timelineGraphic"].includes(event.type) && (
            <div>
              <div className={classes.stats}>
                <div className={`${classes.dataField} ${classes.smallDataField}`}>
                  <Typography color="secondary" className={classes.summaryKeys}>
                    {t("Vehicles.Detail.History.ID")}
                  </Typography>
                </div>
                <div className={classes.dataField}>
                  <Typography className={classes.eventDescription}>{t(event.id)}</Typography>
                </div>
              </div>
              <div className={classes.stats}>
                <div className={`${classes.dataField} ${classes.smallDataField}`}>
                  <Typography color="secondary" className={classes.summaryKeys}>
                    {t("Vehicles.Detail.History.Module")}
                  </Typography>
                </div>
                <div className={classes.dataField}>
                  <Typography className={classes.eventDescription}>
                    {capitalizeFirstWord(t(moduleMap[event.module]))}
                  </Typography>
                </div>
              </div>
            </div>
          )}
        </div>
        <IconButton
          onClick={() => handleClick(event)}
          edge="start"
          color="inherit"
          classes={{
            root: classes.myLocation,
          }}
          disabled={!event.location}
        >
          <MyLocation />
        </IconButton>
      </div>
    </AccordionDetails>
  );
};

TimelineAccordionDetails.propTypes = {
  handleClick: PropTypes.func.isRequired,
  event: PropTypes.instanceOf(TimelineEvent).isRequired,
  isVisible: PropTypes.bool.isRequired,
  timelineStatus: PropTypes.object.isRequired,
};

export default TimelineAccordionDetails;
